<template>
    <ValidationForm :onSubmit="submit">
        <div class="form-group">
            <label for="txtName">Name</label>
            <input type="text" class="form-control" id="txtName" v-model="device.name" />
        </div>
        <div class="form-group">
            <label for="ddlDeviceType">Device Type</label>
            <select class="form-control" id="ddlDeviceType" v-model="device.device_type_id" @change="deviceTypeChange">
                <option v-for="device_type in filtered_device_types" :key="device_type.device_type_id"
                    v-bind:value="device_type.device_type_id">{{ device_type.name }}</option>
            </select>
        </div>
        <div class="form-group" v-if="showSubtypes">
            <label for="ddlDeviceSubtype">Device Subtype</label>
            <select class="form-control" id="ddlDeviceSubtype" v-model="device.device_subtype_id">
                <option :value=null>Select a subtype...</option>
                <option v-for="device_subtype in device_subtypes" :key="device_subtype.device_subtype_id"
                    v-bind:value="device_subtype.device_subtype_id">{{ device_subtype.name }}</option>
            </select>
        </div>
        <div class="form-group" v-if="isVisual">
            <label for="txtPassword">Verification Code</label>
            <input type="text" class="form-control" id="txtPassword" v-model="device.password" />
        </div>
        <div class="form-group" v-if="isSip">
            <label for="ddlDeviceSipType">Sip Type</label>
            <select class="form-control" id="ddlDeviceSipType" v-model="device.device_sip_type_id"
                @change="deviceSipTypeChange">
                <option v-for="device_sip_type in device_sip_types" :key="device_sip_type.device_sip_type_id"
                    v-bind:value="device_sip_type.device_sip_type_id">{{ device_sip_type.name }}</option>
            </select>
        </div>
        <div class="form-check" v-if="showVoip">
            <input type="checkbox" class="form-check-input" v-model="device.voip" @change="deviceVoipChange" />
            <label class="form-check-label" for="chkVoip">VoIP</label>
        </div>
        <div class="form-group" v-if="isExternalSip">
            <label for="ddlIntercomExternalSip">Intercom External SIP</label>
            <select class="form-control" id="ddlIntercomExternalSip" v-model="device.intercom_external_sip_id">
                <option value="0">Select..</option>
                <option v-for="intercom_external_sip in intercom_external_sips"
                    :key="intercom_external_sip.intercom_external_sip_id"
                    v-bind:value="intercom_external_sip.intercom_external_sip_id">{{ intercom_external_sip.name }}
                </option>
            </select>
        </div>
        <div class="form-group" v-if="showZones">
            <label for="ddlZones">Zones</label>
            <v-select multiple v-model="zones_selected" :options="zones_for_drop_down"></v-select>
        </div>
        <div class="form-group">
            <label for="txtIp">IP</label>
            <input type="text" class="form-control" id="txtIp" v-model="device.ip" />
        </div>
        <div class="form-group" v-if="showCameraIp">
            <label for="txtCameraIp">Camera IP</label>
            <input type="text" class="form-control" id="txtCameraIp" v-model="device.camera_ip" />
        </div>
        <div class="form-group" v-if="showPort">
            <label for="txtPort">Port</label>
            <input type="text" class="form-control" id="txtPort" v-model="device.port" />
        </div>
        <ValidationField v-if="showExtension" id="txtExtension" label="Extension"
            helperText="Must be four digits. Cannot start with a 6 or a 9." v-model="device.extension"
            :validator="validate"></ValidationField>
        <div class="form-group" v-for="device_attribute in device.device_attributes" :key="device_attribute.attr_name">
            <label :for="'txt' + device_attribute.attr_name">{{ device_attribute.attr_name }}</label>
            <div v-if="device_attribute.attr_name === 'DID Number' || device_attribute.attr_name === 'Forward Number'">
                <vue-tel-input :onlyCountries="['US']" :validCharactersOnly="true"
                    v-model="device_attribute.attr_str"></vue-tel-input>
            </div>
            <div v-else>
                <input type="text" class="form-control" :id="'txt' + device_attribute.attr_name"
                    v-model="device_attribute.attr_str" />
            </div>
        </div>
        <button type="submit" class="btn btn-hub-primary">Submit</button>
        <router-link :to="{ name: 'Devices' }" class="btn btn-secondary">Cancel</router-link>
    </ValidationForm>
</template>

<script>
import { mapGetters } from 'vuex'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import ValidationMixin from '../../mixins/ValidationMixin.vue'
import DeviceListMixin from '../../mixins/DeviceListMixin.vue'
import { DeviceExtensionValidator } from '../../assets/js/validators'
import { DEVICE_TYPE, DEVICE_SIP_TYPE, ZONE_TYPE, DEVICE_SUBTYPE } from '../../assets/js/resource-types'

const initialZonesSelected = [
    { value: 0, label: 'All Call - District & Site' },
]

export default {
    props: ['device'],
    components: { vSelect },
    mixins: [ValidationMixin, DeviceListMixin],
    data: function () {
        return {
            zones_selected: initialZonesSelected
        }
    },
    computed: {
        ...mapGetters({
            zones: 'allZones'
        }),
        device_subtypes() {
            const device_type = this.filtered_device_types.find(dt => dt.device_type_id === this.device.device_type_id);
            return device_type ? device_type.subtypes : []
        },
        showPort() {
            return [
                DEVICE_TYPE.RTP,
                DEVICE_TYPE.Multicast,
                DEVICE_TYPE.Barionet
            ].includes(this.device.device_type_id)
        },
        showVoip() {
            return (
				this.feature_voip_enabled &&
				this.device.device_type_id === DEVICE_TYPE.SIP &&
				this.device.device_sip_type_id === DEVICE_SIP_TYPE.Generic
            )
        },
        showZones() {
            return ![
                DEVICE_TYPE.Barionet,
                DEVICE_TYPE.Visual
            ].includes(this.device.device_type_id)
        },
        showSubtypes() {
            return this.device_subtypes.length > 0
        },
        showExtension() {
            return this.isSip || this.device.device_type_id === DEVICE_TYPE.G2IPSpeaker || this.device.device_type_id === DEVICE_TYPE.G2Link
        },
        showCameraIp() {
            return this.device.device_type_id === DEVICE_TYPE.G2IPSpeaker && this.device.device_subtype_id === DEVICE_SUBTYPE.ClockCamera
        },
        isSip() {
            return this.device.device_type_id === DEVICE_TYPE.SIP
        },
        isExternalSip() {
            return this.isSip && this.device.device_sip_type_id === DEVICE_SIP_TYPE.External
        },
        isVisual() {
            return this.device.device_type_id === DEVICE_TYPE.Visual
        },
        zones_for_drop_down: function () {
            if (this.isVisual) {
                return this.zones
                    .filter(
                        zone =>
                            zone.site_id === this.scoped_site_id && zone.zone_type_id === ZONE_TYPE.Visual
                    )
                    .map(zone => {
                        return {
                            value: zone.zone_id,
                            label: zone.name
                        }
                    })
            } else {
                return this.zones
                    .filter(
                        zone =>
                            zone.site_id === this.scoped_site_id && zone.zone_type_id !== ZONE_TYPE.Visual
                    )
                    .map(zone => {
                        return {
                            value: zone.zone_id,
                            label: zone.name
                        }
                    })
            }
        }
    },
    created() {
        this.$store.dispatch('getZones')
    },
    methods: {
        deviceTypeChange() {
            this.device.voip = 0
            this.device.device_subtype_id = null
            this.device.device_attributes = []
            this.zones_selected = initialZonesSelected
            this.device.ip = ''

            if ([DEVICE_TYPE.Barionet, DEVICE_TYPE.Visual].includes(this.device.device_type_id)) {
                this.zones_selected = []

                if (this.device.device_type_id === DEVICE_TYPE.Barionet) {
                    this.device.port = '12302'
                    let attributes = new Map()

                    attributes = new Map([
                        ['Public IP', '']
                    ])

                    for (let [attrName, attrStr] of attributes) {
                        this.device.device_attributes.push({
                            attr_name: attrName,
                            attr_str: attrStr
                        })
                    }
                }
            }

            if ([DEVICE_TYPE.SIP, DEVICE_TYPE.Visual, DEVICE_TYPE.G2IPSpeaker, DEVICE_TYPE.G2Link].includes(this.device.device_type_id)) {
                this.device.ip = '1.1.1.1'
            }
        },
        deviceSipTypeChange() {
            this.device.voip = 0
            this.device.device_attributes = []
            let attributes = new Map()

            if (this.device.device_sip_type_id === DEVICE_SIP_TYPE.Advanced) {
                attributes = new Map([
                    ['Command Password', 'Everest'],
                    ['Dev Password', 'verifybravo']
                ])
            } else if (this.device.device_sip_type_id === DEVICE_SIP_TYPE.HUB2) {
                attributes = new Map([
                    ['MAC Address', ''],
                    ['WAN Mode', ''],
                    ['WAN Subnet Mask', ''],
                    ['WAN Gateway', ''],
                    ['Primary DNS', ''],
                    ['Secondary DNS', ''],
                    ['SNTP Server', ''],
                    ['Second SNTP Server', ''],
                    ['Enable SNTP', ''],
                    ['Time Zone', ''],
                    ['Location', ''],
                    ['Time Zone Name', ''],
                    ['Enable DST', ''],
                    ['General Mic Volume', ''],
                    ['Broadcast Speaker Volume', ''],
                    ['Register TTL', ''],
                    ['Enable Registration', ''],
                    ['Building Number', ''],
                    ['Func 1 Value', ''],
                    ['Func 2 Value', '']
                ])
            }

            for (let [attrName, attrStr] of attributes) {
                this.device.device_attributes.push({
                    attr_name: attrName,
                    attr_str: attrStr
                })
            }
        },
        deviceVoipChange() {
            this.device.device_attributes = []
            let attributes = new Map()

            if (this.device.voip) {
                attributes = new Map([
                    ['DID Number', ''],
                    ['Forward Number', '']
                ])
            }

            for (let [attrName, attrStr] of attributes) {
                this.device.device_attributes.push({
                    attr_name: attrName,
                    attr_str: attrStr
                })
            }
        },
        validate(value) {
            const validator = new DeviceExtensionValidator(this.extensions_in_use)
            return validator.validate(value)
        },
        submit() {
            for (let i = 0; i < this.zones_selected.length; i++) {
                this.device.device_zones.push({
                    zone_id: this.zones_selected[i].value
                })
            }
            this.$store.dispatch('addEditDevice', {
                device: this.device,
                push_on_success: 'Devices'
            })
        }
    }
}
</script>